import { createContext, useState, useContext } from "react";

import { UserContext } from "./user.context";

import {
  loadAllNotesFromTask,
  createNote,
  updateNote,
  deleteNoteById,
} from "../../api/notes.api";

export const NoteContext = createContext({});

export const NoteProvider = ({ children }) => {
  const { currentUser } = useContext(UserContext);

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);

  const uid = currentUser?.uid || null;

  const fetchAllNotesFromTask = async (projectIdArg, taskIdArg) => {
    const projectId =
      projectIdArg || localStorage.getItem("selectProjectID") || null;
    const taskId = taskIdArg || localStorage.getItem("selectTaskID") || null;

    if (!currentUser || !uid) return false;
    if (!projectId || !taskId) return false;

    try {
      setNotesLoading(true);
      setNotesError(null);

      const response = await loadAllNotesFromTask(projectId, taskId, uid);

      setNotes(response.notes || []);
      return true;
    } catch (error) {
      console.error(
        "Error loading notes:",
        error?.response?.data || error.message
      );
      setNotesError(error?.response?.data?.message || "Failed to load notes");
      return false;
    } finally {
      setNotesLoading(false);
    }
  };

  const saveNote = async (note) => {
    const projectId = localStorage.getItem("selectProjectID") || null;
    const taskId = localStorage.getItem("selectTaskID") || null;

    if (!uid || !projectId || !taskId) return false;

    try {
      setNotesError(null);

      const notePayload = {
        ...note,
        uid,
      };

      let response;

      if (note.id) {
        response = await updateNote(projectId, taskId, note.id, notePayload);
      } else {
        response = await createNote(projectId, taskId, notePayload);
      }

      const savedNote = response.note;
      if (!savedNote) return false;

      if (note.id) {
        setNotes((prevNotes) =>
          prevNotes.map((currentNote) =>
            currentNote.id === savedNote.id ? savedNote : currentNote
          )
        );
      } else {
        setNotes((prevNotes) =>
          [...prevNotes, savedNote].sort((a, b) =>
            (a.dateCreated || "").localeCompare(b.dateCreated || "")
          )
        );
      }

      return true;
    } catch (error) {
      console.error(
        "Error saving note:",
        error?.response?.data || error.message
      );
      setNotesError(error?.response?.data?.message || "Failed to save note");
      return false;
    }
  };

  const deleteNote = async (noteId) => {
    const projectId = localStorage.getItem("selectProjectID") || null;
    const taskId = localStorage.getItem("selectTaskID") || null;

    if (!uid || !projectId || !taskId || !noteId) return false;

    try {
      setNotesError(null);

      await deleteNoteById(projectId, taskId, noteId, uid);

      setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));

      return true;
    } catch (error) {
      console.error(
        "Error deleting note:",
        error?.response?.data || error.message
      );
      setNotesError(error?.response?.data?.message || "Failed to delete note");
      return false;
    }
  };

  return (
    <NoteContext.Provider
      value={{
        notes,
        setNotes,
        notesLoading,
        notesError,
        fetchAllNotesFromTask,
        saveNote,
        deleteNote,
      }}
    >
      {children}
    </NoteContext.Provider>
  );
};
